import * as moment from 'moment'
import * as _ from 'underscore'

import { OpChangeState, Enums } from '../common/enums'
import { IOpData, ICopyable } from './interfaces'
import { OpDataUtil } from './op-data-util'
import { OpBaseData } from '../common/op-base'

export class OpDataList<T extends IOpData> implements ICopyable<OpDataList<T>> {

    //#region Members

    private list: T[] = []

    private activator: new (obj: any) => T

    //#endregion


    //#region Properties

    public get length(): number { return this.list.length }

    //#endregion


    //#region Ctor

    public constructor(activator: new (obj: any) => T = null) {
        this.activator = activator
    }

    //#endregion

    //#region Public Methods

    public getData(idDataType: number, index: number): T {
        var result = this.list.filter(d => d.idDataType == idDataType && d.indexNBR == index)
        if (result && result.length > 0)
            return result[0]
        return null
    }

    public getDataList(idDataType: number, index: number = null): T[] {
        return this.list.filter(d => d.idDataType == idDataType && (index == null || d.indexNBR == index))
    }

    public getNextIndex(idDataType: number): number {
        const existingData: T[] = this.getDataList(idDataType)

        if (_.size(existingData) > 0) {
            const existingDataIndices: number[] = _.map(existingData, (data: T) => data.indexNBR)

            return _.max(existingDataIndices) + 1
        }

        return 0
    }

    public getValue(idDataType: number, index: number = 0, defaultValue: any = null): any {
        var filteredDataList = this.list.filter(d => d.idDataType == idDataType && d.indexNBR == index)
        if (filteredDataList.length > 0) return filteredDataList[0].value
        return defaultValue
    }

    public push(...args: T[]): number {
        return this.list.push(...args)
    }

    public removeValue(idDataType: number, index: number = 0, idData: number = null) {
        var filteredDataList = this.list.filter(d => d.idDataType == idDataType && d.indexNBR == index)
        if (idData) {
            filteredDataList = filteredDataList.filter(d => d.idData == idData)
        }
        if (filteredDataList.length > 0) {
            var item = filteredDataList[0]
            item.opState = OpChangeState.Delete
        }
    }

    public setValue(value: any, idDataType: number, index: number = 0, idData: number = null, state: Enums.OpChangeState = null) {
        var filteredDataList = this.list.filter(d => d.idDataType == idDataType && d.indexNBR == index)
        if (idData) {
            filteredDataList = filteredDataList.filter(d => d.idData == idData)
        }
        if (filteredDataList.length > 0) {
            var item = filteredDataList[0]

            if (!OpDataUtil.valueEquals(item.value, value)) {
                item.value = value

                if (state == null) {
                    if (item.opState != OpChangeState.New && item.opState != OpChangeState.Delete) {
                        item.opState = OpChangeState.Modified
                    }
                } else {
                    item.opState = state
                }
            }
        } else {
            this.addValue(value, idDataType, index, idData, state)
        }
    }

    public toArray(): T[] {
        return this.list
    }

    public ToJSON(customObject: boolean = false): any {
        return this.list.map((data: T) => {
            var value = data.value

            if (value) {
                if (typeof value === 'object' && typeof value.getTime === 'function') {
                    value = moment(value).format('YYYY-MM-DD HH:mm:ss.SSS')
                }
            }

            if (customObject) {
                return {
                    IdData: data.idData,
                    IdDataType: data.idDataType,
                    IndexNbr: data.indexNBR,
                    OpState: data.opState,
                    Value: value
                }
            }

            return {
                ID_DATA: data.idData,
                ID_DATA_TYPE: data.idDataType,
                INDEX_NBR: data.indexNBR,
                OpState: data.opState,
                VALUE: value
            }
        })
    }

    // Public Methods End


    //#region ICopyable Implementation

    public clone(includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): OpDataList<T> {
        const newDataList: OpDataList<T> = new OpDataList<T>()

        newDataList.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return newDataList
    }

    public copy(source: OpDataList<T>, includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): void {
        if (source == null) {
            return null
        }

        if (_.isArray(this.list)) {

            // Zmienić na klonowanie z ICopyable jak wszystkie data go zimplementują 
            this.list = _.map(source.list, (data: T) => {
                if (_.isFunction((data as any).clone)) {
                    return (data as any).clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
                }

                return {
                    idData: data.idData,
                    idDataType: data.idDataType,
                    indexNBR: data.indexNBR,
                    opState: data.opState,
                    value: data.value,
                }
            })
        }
    }

    //#endregion


    //#region Private Methods

    private addValue(value: any, idDataType: number, index: number, idData: number, state: Enums.OpChangeState = null) {
        const newItem: IOpData = this.activator == null
            ? new OpBaseData()
            : new this.activator(null)

        newItem.idDataType = idDataType
        newItem.indexNBR = index
        newItem.value = value
        newItem.opState = state == null
            ? OpChangeState.New
            : state

        if (idData != null) {
            newItem.idData = idData
        }

        this.list.push(<any>newItem)
    }

    //#endregion
}